import { useState, useEffect, useCallback } from 'react';
import { enhancedApi } from '../../../shared/services/enhancedApi';
import { useAppStore } from '@/store/useAppStore';

export interface IDTypes {
  id: number;
  name: string;
  isNonFilipino: boolean;
}

interface UseIDTypesReturn {
  validIds: IDTypes[];
  idTypesOptions: IDTypes[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useIDTypes = (): UseIDTypesReturn => {
  const [validIds, setIDTypes] = useState<IDTypes[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { setValidIds } = useAppStore();

  const fetchIDTypes = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use enhanced API with caching and queuing
      const response = await enhancedApi.cachedGet<{
        success: boolean;
        validIds: IDTypes[];
      }>(
        '/api/ids',
        {},
        30 * 60 * 1000 // 30 minutes TTL - ID types rarely change
      );

      if (response.success && response.validIds) {
        // sort A → Z by name
        const sortedIDTypes = response.validIds.sort(
          (a: IDTypes, b: IDTypes) => a.name.localeCompare(b.name)
        );

        setIDTypes(sortedIDTypes);
        setValidIds(sortedIDTypes);
        console.log('Fetched ID types (cached/sorted):', sortedIDTypes);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching valid IDs:', err);
      setError('Failed to fetch valid IDs');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed since api and setters are stable

  useEffect(() => {
    fetchIDTypes();
  }, [fetchIDTypes]); // ✅ Now includes the dependency

  // Convert ID types to options format for dropdowns (already sorted, remove duplicates)
  const idTypesOptions = validIds;

  return {
    validIds,
    idTypesOptions,
    loading,
    error,
    refetch: fetchIDTypes
  };
};